/**
 * Nigerian state / LGA lookups behind the list-sourced custom-field dropdowns
 * (#574). Mirrors App\Services\ReferenceData\NigerianStates on the backend:
 * matching ignores case and extra spaces, and "Abuja"/"FCT" name the Federal
 * Capital Territory.
 */

import type { NigerianState } from '@/types/composition-functions';

export type NigerianStateOption = Pick<NigerianState, 'name' | 'lgas'>;

const normalise = (value: string | null | undefined): string =>
  (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

const FCT_ALIASES = ['fct', 'abuja', 'fct abuja'];

export function findState(states: NigerianStateOption[], name: string | null | undefined): NigerianStateOption | undefined {
  const key = normalise(name);
  if (key === '') return undefined;
  const target = FCT_ALIASES.includes(key) ? 'federal capital territory' : key;
  return states.find(s => normalise(s.name) === target);
}

/** LGAs of a state, or an empty list when the state is unknown. */
export function lgasForState(states: NigerianStateOption[], name: string | null | undefined): string[] {
  return findState(states, name)?.lgas ?? [];
}

/** Every LGA once, alphabetically — for an LGA field that follows no state field. */
export function allLgas(states: NigerianStateOption[]): string[] {
  return [...new Set(states.flatMap(s => s.lgas))].sort((a, b) => a.localeCompare(b));
}

/** Whether an LGA still fits the chosen state — when not, the LGA must be cleared. */
export function lgaBelongsToState(states: NigerianStateOption[], state: string | null | undefined, lga: string | null | undefined): boolean {
  if (!lga) return true;
  const key = normalise(lga);
  return lgasForState(states, state).some(l => normalise(l) === key);
}
